import { useEffect, useState, Fragment } from 'react';
import { UserSessionManager } from '~/services/auth/UserSessionManager';
import { cn } from '../ui/utils';
import { Link, useLocation } from '@remix-run/react';
import { Logo } from '../logo';
import { Routes } from '~/constants/routes';
import { Button } from '../ui/button';
import { Menu, X } from 'lucide-react';
import { Transition } from '@headlessui/react';

const navItems = [
  {
    label: 'Docs',
    to: Routes.DocsBrokkrOverview,
  },
  {
    label: 'Inventory',
    to: Routes.PublicInventory,
  },
  {
    label: 'Learn More',
    to: 'https://hydrahost.com',
  },
];

interface NavbarProps {
  user?: UserSessionManager;
}

export function Navbar(props: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <nav
        className={cn(
          'fixed inset-x-0 top-0 z-50 flex items-center justify-between px-4 md:px-6 py-4 transition-all duration-300 border-b border-purple/20',
          scrolled
            ? 'bg-background/95 backdrop-blur-lg shadow-lg'
            : 'bg-gradient-to-b from-background/80 to-transparent backdrop-blur-sm',
        )}
      >
        <div className="flex items-center gap-4 md:gap-6">
          <Link
            to={Routes.PublicInventory}
            className="group transition-transform hover:scale-105"
          >
            <Logo className="h-8 w-auto -mt-2" />
          </Link>

          <div className="hidden md:flex items-center gap-4">
            {navItems.map((item) => {
              const isCurrent = location.pathname.includes(item.to);

              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={cn(
                    isCurrent
                      ? 'bg-purple-darker text-white'
                      : 'bg-transparent hover:bg-purple-darker',
                    'rounded-md px-3 py-2 text-sm font-semibold',
                  )}
                  viewTransition
                  prefetch="intent"
                >
                  {item.label}
                </Link>
              );
            })}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {props.user ? (
            <Link
              to={Routes.Deployments}
              className="hidden md:block rounded-md px-3 py-2 text-sm font-semibold bg-primary"
            >
              Go to Deployments
            </Link>
          ) : (
            <div className="hidden md:flex items-center gap-3">
              <Link
                to={Routes.Login}
                className={cn(
                  'rounded-md px-3 py-2 text-sm font-semibold hover:bg-purple-darker',
                )}
                viewTransition
                prefetch="intent"
              >
                Log In
              </Link>

              <Link
                to={Routes.SignUp}
                className={cn(
                  'rounded-md px-3 py-2 text-sm font-semibold bg-primary',
                )}
                viewTransition
                prefetch="intent"
              >
                Sign Up
              </Link>
            </div>
          )}

          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <Menu className="h-5 w-5" />
            <span className="sr-only">Toggle navigation menu</span>
          </Button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <Transition.Root show={mobileMenuOpen} as={Fragment}>
        <div className="fixed inset-0 z-50 md:hidden">
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              className="fixed inset-0 bg-background/80 backdrop-blur-sm"
              onClick={() => setMobileMenuOpen(false)}
            />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="transform transition ease-in-out duration-300"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transform transition ease-in-out duration-300"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <div className="fixed right-0 top-0 h-full w-full max-w-xs bg-background border-l border-border shadow-xl">
              <div className="flex items-center justify-between p-4 border-b border-border">
                <Logo className="h-8 w-auto" />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <X className="h-5 w-5" />
                  <span className="sr-only">Close menu</span>
                </Button>
              </div>

              <nav className="p-4 space-y-2">
                {navItems.map((item) => {
                  const isCurrent = location.pathname.includes(item.to);

                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        'block px-3 py-2 rounded-md text-base font-semibold transition-colors',
                        isCurrent
                          ? 'bg-primary/10 text-primary'
                          : 'text-muted-foreground hover:text-white hover:bg-muted',
                      )}
                      viewTransition
                      prefetch="intent"
                    >
                      {item.label}
                    </Link>
                  );
                })}

                <div className="pt-6 space-y-2 border-t border-border">
                  {props.user ? (
                    <Link
                      to={Routes.Deployments}
                      onClick={() => setMobileMenuOpen(false)}
                      className="block"
                    >
                      <Button variant="ghost" className="w-full">
                        Go to Deployments
                      </Button>
                    </Link>
                  ) : (
                    <>
                      <Link
                        to={Routes.Login}
                        onClick={() => setMobileMenuOpen(false)}
                        className="block"
                      >
                        <Button variant="outline" className="w-full">
                          Log In
                        </Button>
                      </Link>
                      <Link
                        to={Routes.SignUp}
                        onClick={() => setMobileMenuOpen(false)}
                        className="block"
                      >
                        <Button className="w-full">Sign Up</Button>
                      </Link>
                    </>
                  )}
                </div>
              </nav>
            </div>
          </Transition.Child>
        </div>
      </Transition.Root>
    </>
  );
}
